import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '../client';
import { useAuth } from '../../auth/useAuth';

export interface VersionSummary {
  id: string;
  createdAt: string;
  createdBy?: string | null;
  label?: string | null;
  stage?: string | null;
  linkedOutlineVersionId?: string | null;
  sourceModel?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useVersions(bookId: string | undefined) {
  const { getToken } = useAuth();
  return useQuery<VersionSummary[]>({
    enabled: !!bookId,
    queryKey: ['versions', bookId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const response = await client
        .get(`api/v2/books/${bookId}/versions`)
        .json<ApiResponse<VersionSummary[]>>();
      return response.data ?? [];
    },
  });
}

export function useRestoreVersion(bookId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (versionId: string) => {
      if (!bookId) throw new Error('bookId required');
      const client = createApiClient(getToken);
      await client.post(
        `api/v2/books/${bookId}/versions/${versionId}/restore`,
        { json: {} },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chapters', bookId] });
      queryClient.invalidateQueries({ queryKey: ['versions', bookId] });
      queryClient.invalidateQueries({ queryKey: ['book', bookId] });
    },
  });
}

export function useLabelBookVersion(bookId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      versionId: string;
      label?: string | null;
      stage?: string | null;
    }) => {
      const client = createApiClient(getToken);
      await client.patch(
        `api/v2/books/${bookId}/versions/${input.versionId}/label`,
        { json: { label: input.label, stage: input.stage } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versions', bookId] });
    },
  });
}
